import classNames from 'classnames';
import { toSlug, useRouter } from '@medifind/router';
import { MFLazyLoad, MFLink, Select } from '@medifind/shared-basic-components';
import { getValueMediaBreakpointDown } from '@medifind/utils';
import { selectRouteParams, useRouteStore } from '@medifind/zustand';
import styles from './ConditionRollups.module.scss';

const ConditionRollupsLinks = ({ links = [] }) => {
  return links.map((child, i) => {
    const { display, projectId } = child || {};
    return (
      projectId && (
        <span key={i}>
          {i > 0 && ', '}
          <MFLink to={`/conditions/${toSlug(display)}/${projectId}/doctors`} className={classNames(styles['condition-rollups__link'])}>
            {display}
          </MFLink>
        </span>
      )
    );
  });
};

const ConditionRollups = ({ totalResults, data }) => {
  const router = useRouter();
  const { codingName } = useRouteStore(selectRouteParams);
  const { parentConditions, childConditions } = data;
  const allConditions = [...parentConditions, ...childConditions];

  const handleChildConditionsChange = (event) => {
    const { display, projectId } = event;
    projectId && router.history.push(`/conditions/${toSlug(display)}/${projectId}/doctors`);
  };

  const heading = (() => {
    // We show related conditions (allConditions) if there is 0 or just a few results
    if (totalResults === 0) {
      if (allConditions?.length > 0)
        return `There are no results for ${codingName} in your selected location. Please try expanding your location or viewing a related condition listed below:`;
      else return `There are no results for ${codingName} in your selected location. Please expand your search radius.`;
    } else if (totalResults <= 10) {
      if (allConditions?.length > 0) return `To see more doctors, please expand your search radius or view related conditions below.`;
      else return `To see more doctors for ${codingName}, please expand your search radius.`;
      // Otherwise only the specific types (childConditions)
    } else return `Are you looking for a specific type of ${codingName}?`;
  })();

  const { links, items } = (() => {
    if (allConditions.length === 0 && totalResults === 0) {
      return { links: [], items: [] };
    }
    // We show related conditions (allConditions) if there is 0 or just a few results, otherwise only the children (childConditions)
    const conditions = totalResults <= 10 ? allConditions : childConditions;
    const numberOfConditionsToShow = conditions.length == 5 ? 5 : 4; // 4 and rollover the rest to a select box, unless there is exactly 5
    return {
      links: conditions.slice(0, numberOfConditionsToShow),
      items: conditions.slice(numberOfConditionsToShow),
    };
  })();

  return (
    <>
      {links.length > 0 && (
        <MFLazyLoad noSuspense offset={100} height={getValueMediaBreakpointDown({ xs: 448, sm: 448, md: 244, default: 244 })}>
          <div
            className={classNames(styles['condition-rollups__container'], {
              [styles['no-results']]: totalResults === 0,
            })}
          >
            <p
              className={classNames(styles['condition-rollups__heading'], {
                [styles['no-results']]: totalResults === 0,
              })}
            >
              {heading}
            </p>
            <p className={styles['condition-rollups__text']}>
              {totalResults > 10 && 'Common conditions include: '}
              <ConditionRollupsLinks links={links} />
            </p>
            {items.length > 0 && (
              <Select
                id="condition-rollups"
                placeholder="Select another"
                onChange={handleChildConditionsChange}
                items={items}
                selectValue={{ display: 'Select another', value: '' }}
                classes={{ menuWrapper: styles['condition-rollups__select'] }}
              />
            )}
          </div>
        </MFLazyLoad>
      )}
    </>
  );
};

export { ConditionRollups as default, ConditionRollups };
